import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { SourceSheet } from "@/components/SourceSheet";
import { SessionMobile } from "@/components/session/SessionMobile";
import { SessionDesktop } from "@/components/session/SessionDesktop";
import { FlowView } from "@/components/session/FlowView";
import { Closing } from "@/components/session/Closing";

import { useIsDesktop } from "@/hooks/useIsDesktop";
import { attachStream, detachStream, micLevel, resume } from "@/lib/audioLevel";
import { ORIGIN, api, errMsg } from "@/lib/api";
import { ConnectionBanner } from "@/components/ConnectionBanner";
import { RealtimeVoice, isIOS, realtimeSupported, releaseMic } from "@/lib/realtimeVoice";
import { isNetworkError, onConnection, setDropped, withRetry } from "@/lib/connection";

const LANGS = [
  { key: "auto", short: "Auto", label: "Auto-detect" },
  { key: "en", short: "EN", label: "English" },
  { key: "sw", short: "SW", label: "Kiswahili" },
];

const TICK = 60;
const SPEAK_ON = 0.055;   // mic level that counts as speech
const SPEAK_OFF = 0.045;  // below this we're counting silence
const BARGE = 0.12;       // interrupting Griot mid-sentence
const SILENCE_END = 1100; // ms of quiet that ends a turn
const MIN_SPEECH = 400;   // ignore coughs and door slams
const MAX_UTTERANCE = 30000;
const AWAY_GRACE = 45000;  // how long an open line waits while the phone is locked or the tab is away

export default function Session() {
  const nav = useNavigate();
  const loc = useLocation();
  const isDesktop = useIsDesktop();
  const sessionId = loc.state?.sessionId;

  const [state, setState] = useState("idle");
  const [mode, setModeRaw] = useState(loc.state?.text === true ? "text" : "voice");
  const [lang, setLang] = useState(localStorage.getItem("griot-language") || loc.state?.language || "auto");
  const [turns, setTurns] = useState([]);
  const [openings, setOpenings] = useState([]);
  const [sheet, setSheet] = useState(null);
  const [draft, setDraft] = useState("");
  const [convo, setConvo] = useState(loc.state?.flow !== false && loc.state?.text !== true);
  const [elapsed, setElapsed] = useState(0);
  const [read, setRead] = useState(() => loc.state?.text === true || localStorage.getItem("griot-read-mode") === "1");
  const [passages, setPassages] = useState(!!loc.state?.sourcesOpen);
  const [muted, setMuted] = useState(false);
  const [greeting, setGreeting] = useState("");
  const [notes, setNotes] = useState([]);
  const [shown, setShown] = useState([]);   // everything he put on screen this sitting, for the keepsake
  const [awayClosed, setAwayClosed] = useState(false);
  const [closing, setClosing] = useState(false);
  const [kept, setKept] = useState(() => new Set());
  const [awayCopy, setAwayCopy] = useState("");
  const [needsTap, setNeedsTap] = useState(false);
  const [volume, setVolume] = useState(() => Number(localStorage.getItem("griot-volume") || 0.7));
  const [output, setOutput] = useState(() => localStorage.getItem("griot-output") || "speaker");
  const away = useRef(null);
  const volumeRef = useRef(0.7);
  const nameRef = useRef(localStorage.getItem("griot-name") || "");
  const outputRef = useRef("speaker");
  const lastNote = useRef({ at: 0, group: "" });
  const notesRef = useRef([]);
  const keepAll = useRef(false);      // a phone shows the spool, which lets nothing go

  const quiet = useRef(Date.now());   // when Griot last stopped speaking (0 = still speaking)
  const released = useRef(0);
  const [pending, setPending] = useState(null); // a turn the dropped line swallowed

  const rec = useRef(null);
  const chunks = useRef([]);
  const audio = useRef(null);
  const stream = useRef(null);
  const bottom = useRef(null);
  const cancelled = useRef(false);
  const speech = useRef(0);
  const silence = useRef(0);
  const recStart = useRef(0);
  const playStart = useRef(0);
  const stateRef = useRef(state);
  const convoRef = useRef(convo);
  const langRef = useRef(lang);
  const readRef = useRef(read);
  const mutedRef = useRef(muted);
  const greeted = useRef(false);
  const heard = useRef([]); // welcomes already used this sitting
  const greetings = useRef([]);
  const live = useRef(null); // the open realtime line, when flowing runs on it
  const liveOn = useRef(false);
  const opening = useRef(false);
  const fellBack = useRef(false); // the real voice ran out; the line was reopened on the understudy
  stateRef.current = state;
  convoRef.current = convo;
  langRef.current = lang;
  readRef.current = read;
  mutedRef.current = muted;
  notesRef.current = notes;
  keepAll.current = !isDesktop;
  volumeRef.current = volume;
  outputRef.current = output;

  // A note never leaves while Griot is still saying the point. Once it has gone quiet and the words
  // have had a moment to land, the notes are released one after another and fly off across the screen.
  const pushNote = (note) => {
    const id = `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
    const at = Date.now();
    // points that arrive together belong together, however the model chose to send them
    const joins = ["idea", "step"].includes(note.kind) && at - lastNote.current.at < 9000 && lastNote.current.group;
    const group = note.group || (joins ? lastNote.current.group : `g${at}`);
    if (["idea", "step"].includes(note.kind)) lastNote.current = { at, group };
    setNotes((n) => {
      const words = (note.text || "").trim().toLowerCase();
      if (n.some((x) => x.text.trim().toLowerCase() === words)) return n;   // never twice on screen
      const mine = n.filter((x) => x.group === group);
      const seat = mine[0]?.seat ?? n.length % 2;
      const room = keepAll.current ? n.slice(-39) : n.slice(-4);
      return [...room, { id, ...note, group, seat, born: at, index: note.index || mine.length + 1 }];
    });
    // the keepsake remembers everything he put on screen, not just what has not drifted off yet
    setShown((s) => {
      const words = (note.text || "").trim().toLowerCase();
      if (!words || s.some((x) => x.text.trim().toLowerCase() === words)) return s;
      return [...s.slice(-39), { id, ...note, group, born: at }];
    });
  };
  useEffect(() => {
    if (process.env.NODE_ENV === "production") return;
    window.__griotNote = pushNote;
    window.__griotNotes = () => ({ quiet: quiet.current, now: Date.now(), notes: notesRef.current.map((n) => ({ t: n.text, leaving: !!n.leaving, age: Date.now() - n.born })) });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (state === "speaking") quiet.current = 0; else if (!quiet.current) quiet.current = Date.now(); }, [state]);
  useEffect(() => {
    const t = setInterval(() => {
      const now = Date.now();
      const next = notesRef.current.find((x) => !x.leaving);
      if (!next || now - released.current < 800) return;
      // it stays until he has actually finished saying it: a full turn of quiet, and eight seconds
      // on screen at the very least. A third note arriving pushes the oldest on its way.
      const crowded = notesRef.current.filter((x) => !x.leaving).length > 2;
      if (keepAll.current) return;   // on a phone the spool keeps everything; nothing is released
      const said = quiet.current && now - quiet.current > 2200 && now - next.born > 8000;
      if (!said && !crowded && now - next.born < 70000) return;   // stranded note: let it go anyway
      released.current = now;
      setNotes((n) => n.map((x) => (x.id === next.id ? { ...x, leaving: true } : x)));
      setTimeout(() => setNotes((n) => n.filter((x) => x.id !== next.id)), 2900);
    }, 300);
    return () => clearInterval(t);
  }, []);

  useEffect(() => { if (!sessionId) nav("/", { replace: true }); }, [sessionId, nav]);
  useEffect(() => { api.openings(loc.state?.labSlug).then((r) => setOpenings(r.items || [])).catch(() => {}); }, [loc.state?.labSlug]);
  useEffect(() => { bottom.current?.scrollIntoView({ behavior: "smooth", block: "end" }); }, [turns, state]);
  useEffect(() => onConnection((c) => { if (c.online && pending) pending(); }), [pending]);
  useEffect(() => {
    const onVisible = () => {
      if (document.hidden) {
        cancelRecording();
        // an open realtime line costs money per minute: hold it briefly, then close it cleanly
        if (live.current && !away.current) {
          away.current = setTimeout(() => {
            away.current = null;
            live.current?.stop();
            live.current = null;
            liveOn.current = false;
            setAwayClosed(true);
            setState("idle");
          }, AWAY_GRACE);
        }
        return;
      }
      if (away.current) { clearTimeout(away.current); away.current = null; }
      setPending(null);
      setDropped(false);
      if (convoRef.current && ["listening", "reflecting"].includes(stateRef.current)) {
        cancelRecording();
        resume();
        setState("idle");
      }
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => () => {
    audio.current?.pause();
    live.current?.stop();
    stream.current?.getTracks().forEach((t) => t.stop());
    releaseMic();
    detachStream();
  }, []);

  const getStream = async () => {
    const live = stream.current?.getTracks().some((t) => t.readyState === "live");
    if (live) return stream.current;
    const s = await navigator.mediaDevices.getUserMedia({
      audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
    });
    stream.current = s;
    attachStream(s);
    return s;
  };

  // Griot's voice arrives as a stream, so playback starts on the first few KB rather than
  // waiting for the whole file. src is a URL; base64 is still honoured (the cached welcome).
  const srcOf = (piece) => (piece?.url ? `${ORIGIN}${piece.url}` : piece?.b64 ? `data:audio/mp3;base64,${piece.b64}` : null);

  const afterTurn = (r) => {
    if (r?.note?.text) pushNote(r.note);
    return r;
  };

  const play = (piece, tailFetch) =>
    new Promise((res) => {
      const src = srcOf(piece);
      if (!src) return res();
      let stopped = false;
      const tail = tailFetch ? tailFetch() : null; // synthesised while this plays
      const start = (url, next, retried) => {
        const a = new Audio();
        a.preload = "auto";
        a.src = url;
        audio.current = a;
        playStart.current = Date.now();
        const done = () => (next && !stopped ? next() : res());
        // some browsers refuse a chunked stream — fall back to the whole file once
        const recover = async () => {
          if (retried || stopped || !url.startsWith("http")) return done();
          try {
            const blob = await fetch(url).then((r) => r.blob());
            start(URL.createObjectURL(blob), next, true);
          } catch { done(); }
        };
        a.onended = done;
        a.onerror = recover;
        a.onpause = () => { if (!a.ended) { stopped = true; res(); } };
        a.play().catch(recover);
      };
      const then = tail
        ? async () => {
            const nextSrc = srcOf(await tail);
            if (stopped || !nextSrc) return res();
            start(nextSrc, null);
          }
        : null;
      start(src, then);
    });

  // the answer arrives in two pieces: the opening fragment, then the rest while it plays
  const appendTail = (text) => {
    if (!text) return;
    setTurns((t) => {
      const last = t[t.length - 1];
      if (!last || last.role !== "griot") return t;
      return [...t.slice(0, -1), { ...last, text: `${last.text} ${text}`.trim() }];
    });
  };

  const handleResult = async (r) => {
    setTurns((t) => [...t, { role: "griot", text: r.text, sources: r.sources }]);
    afterTurn(r);
    if (r.audio_url || r.audio_b64) {
      setState("speaking");
      await play(
        { url: r.audio_url, b64: r.audio_b64 },
        r.has_tail
          ? () => api.turnTail(sessionId).then((x) => { appendTail(x.text); return { url: x.audio_url, b64: x.audio_b64 }; }).catch(() => null)
          : null,
      );
    } else if (r.has_tail) {
      api.turnTail(sessionId).then((x) => appendTail(x.text)).catch(() => {});
    }
    setState("idle");
  };

  const lineIsUp = async () => {
    try { await api.status(loc.state?.labSlug); return true; } catch { return false; }
  };

  const fail = async (e, retry) => {
    const code = e?.response?.status;
    const d = e?.response?.data?.detail;
    setState("idle");
    if (code === 503 && ["maintenance", "capped", "closed"].includes(d)) return nav(`/fallback/${d}`);
    if (isNetworkError(e) && retry) {
      // never claim the line dropped without checking it — a backgrounded tab looks the same
      if (await lineIsUp()) {
        setDropped(false);
        setPending(null);
        if (convoRef.current) toast("I lost that one — say it again when you're ready.");
        return;
      }
      setPending(() => retry);
      setDropped(true);
      return;
    }
    toast.error(errMsg(e));
  };

  const send = async (fn) => {
    setState("reflecting");
    try {
      await handleResult(await withRetry(fn));
      setPending(null);
    } catch (e) { fail(e, () => send(fn)); }
  };

  const beginRecording = async () => {
    try {
      const s = await getStream();
      resume();
      const mime = MediaRecorder.isTypeSupported("audio/webm;codecs=opus") ? "audio/webm;codecs=opus" : "";
      const mr = new MediaRecorder(s, mime ? { mimeType: mime } : undefined);
      chunks.current = [];
      cancelled.current = false;
      speech.current = 0;
      silence.current = 0;
      recStart.current = Date.now();
      mr.ondataavailable = (e) => e.data.size && chunks.current.push(e.data);
      mr.onstop = async () => {
        rec.current = null;
        setElapsed(0);
        if (cancelled.current) { setState("idle"); return; }
        const blob = new Blob(chunks.current, { type: mr.mimeType || "audio/webm" });
        if (blob.size < 1200) { setState("idle"); return; }
        let pushed = false;
        const sendVoice = async () => {
          const r = await api.sendVoice(sessionId, blob, langRef.current, !readRef.current);
          if (!pushed) { setTurns((t) => [...t, { role: "user", text: r.transcript }]); pushed = true; }
          return r;
        };
        await send(sendVoice);
      };
      mr.start();
      rec.current = mr;
      setState("listening");
    } catch {
      setConvo(false);
      toast.error("Microphone access is needed for voice. You can type instead.");
    }
  };

  const stopRecording = () => rec.current?.state === "recording" && rec.current.stop();

  const cancelRecording = () => {
    cancelled.current = true;
    stopRecording();
  };

  // one loop: recording timer, silence detection and barge-in
  useEffect(() => {
    const id = setInterval(() => {
      const s = stateRef.current;
      if (s === "listening") setElapsed(Date.now() - recStart.current);
      if (!convoRef.current || mutedRef.current || liveOn.current) return;
      const lvl = micLevel();

      if (s === "idle") {
        speech.current = lvl > SPEAK_ON ? speech.current + TICK : 0;
        if (speech.current >= 120) beginRecording();
        return;
      }
      if (s === "listening") {
        if (lvl > SPEAK_OFF) { speech.current += TICK; silence.current = 0; }
        else silence.current += TICK;
        const long = Date.now() - recStart.current > MAX_UTTERANCE;
        if (long || (speech.current > MIN_SPEECH && silence.current > SILENCE_END)) stopRecording();
        return;
      }
      if (s === "speaking") {
        if (Date.now() - playStart.current < 900) return; // let Griot get a word in
        speech.current = lvl > BARGE ? speech.current + TICK : 0;
        if (speech.current >= 200) {
          audio.current?.pause();
          speech.current = 0;
          setState("idle");
        }
      }
    }, TICK);
    return () => clearInterval(id);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const stopSpeaking = () => {
    audio.current?.pause();
    speech.current = 0;
    setState("idle");
  };

  const onMic = () => {
    if (state === "speaking") return stopSpeaking();
    if (state === "idle") return beginRecording();
    if (state === "listening") stopRecording();
  };

  const toggleRead = () => {
    const next = !read;
    setRead(next);
    localStorage.setItem("griot-read-mode", next ? "1" : "0");
    if (next) audio.current?.pause();
    if (next && stateRef.current === "speaking") setState("idle");
    toast(next ? "Reading — I'll write my answers and stay quiet" : "Listening — I'll speak my answers");
  };

  const togglePassages = () => {
    setPassages((v) => !v);
  };

  const toggleConvo = async () => {
    if (convo) return leaveFlow();
    setConvo(true);
    await openFlow();
  };

  // Flowing runs on the realtime model: one open line, its own voice detection, no round trips.
  // If it can't be opened we fall back to the request/response loop rather than losing the mode.
  const openFlow = async () => {
    if (live.current || opening.current) return;
    opening.current = true;
    try {
      await _openFlow();
    } finally {
      opening.current = false;
    }
  };

  const _openFlow = async () => {
    if (loc.state?.realtime !== false && realtimeSupported()) {
      const v = new RealtimeVoice({
        sessionId,
        language: langRef.current,
        labName: loc.state?.labName || "",
        output: outputRef.current,
        visitor: nameRef.current,
        onName: ({ name }) => {
          nameRef.current = name;
          if (name) localStorage.setItem("griot-name", name);
          else localStorage.removeItem("griot-name");
        },
        onNeedsTap: (need) => setNeedsTap(need),
        onVoiceFallback: () => {
          // the real voice has run out mid-conversation: reopen the line, which comes back on the
          // understudy, carrying the thread so nothing is restarted
          if (fellBack.current) return;
          fellBack.current = true;
          const gone = live.current;
          live.current = null;
          liveOn.current = false;
          try { gone?.stop(); } catch { /* already closed */ }
          openFlow();
        },
        onLockStop: () => { setMuted(true); mutedRef.current = true; live.current?.interrupt?.(); },
        onState: (s) => { if (!mutedRef.current || s !== "listening") setState(s); },
        onGreeting: (text) => setGreeting(text || ""),
        onUser: (text) => setTurns((t) => [...t, { role: "user", text }]),
        onGriot: ({ text, sources }) => setTurns((t) => [...t, { role: "griot", text, sources }]),
        onNote: pushNote,
        onIdleClose: () => {
          live.current = null;
          liveOn.current = false;
          setAwayCopy("You went quiet for a while, so I let the line rest. Mimi bado hapa — I am still here.");
          setAwayClosed(true);
          setState("idle");
        },
        onError: () => {},
      });
      try {
        await v.start();
        v.setVolume(volumeRef.current);
        v.setOutput(outputRef.current);
        live.current = v;
        if (process.env.NODE_ENV !== "production") window.__griotLive = v; // probe handle for tests
        liveOn.current = true;
        greeted.current = true;
        setState("idle");
        return;
      } catch (e) {
        live.current = null;
        liveOn.current = false;
        const code = e?.response?.status;
        const detail = e?.response?.data?.detail;
        if (code === 503 && ["maintenance", "capped", "closed"].includes(detail)) return nav(`/fallback/${detail}`);
        if (e?.name === "NotAllowedError") { setConvo(false); return toast.error("Microphone access is needed for a flowing conversation."); }
      }
    }
    try {
      await getStream();
      resume();
      setMuted(false);
      greet();
    } catch {
      setConvo(false);
      toast.error("Microphone access is needed for a flowing conversation.");
    }
  };

  useEffect(() => {
    if (sessionId && loc.state?.flow !== false) openFlow();
  }, [sessionId]); // eslint-disable-line react-hooks/exhaustive-deps

  const leaveFlow = () => {
    setConvo(false);
    setMuted(false);
    setGreeting("");
    live.current?.stop();
    live.current = null;
    liveOn.current = false;
    cancelRecording();
    audio.current?.pause();
    stream.current?.getTracks().forEach((t) => t.stop());
    stream.current = null;
    detachStream();
    setState("idle");
  };

  // the browser refused to start his voice: one tap, inside a real gesture, fixes it
  const wakeAudio = () => {
    setNeedsTap(false);
    live.current?.wake?.();
  };

  const changeVolume = (v) => {
    setVolume(v);
    localStorage.setItem("griot-volume", String(v));
    live.current?.setVolume?.(v);
  };

  const changeOutput = (mode) => {
    setOutput(mode);
    localStorage.setItem("griot-output", mode);
    live.current?.setOutput?.(mode);
  };

  // the line was closed while they were away — open a fresh one on their word
  const resumeAway = async () => {
    setAwayClosed(false);
    setAwayCopy("");
    greeted.current = false;
    await openFlow();
  };

  const toggleMute = () => {
    const next = !muted;
    setMuted(next);
    if (live.current) {
      live.current.setMuted(next);
      if (next && stateRef.current === "listening") setState("idle");
      return;
    }
    stream.current?.getAudioTracks().forEach((tr) => (tr.enabled = !next));
    if (next) {
      cancelRecording();
      if (stateRef.current === "listening") setState("idle");
    } else {
      resume();
    }
  };

  // Griot introduces itself the moment a flowing conversation opens — full the first
  // time, one line after that — then falls quiet and listens.
  const greet = async () => {
    try {
      const all = greetings.current.length ? greetings.current : (greetings.current = (await api.greetings(loc.state?.labSlug, langRef.current)).items || []);
      if (!all.length) return;
      if (heard.current.length >= all.length) heard.current = [];
      const left = all.map((_, i) => i).filter((i) => !heard.current.includes(i));
      const pick = left[Math.floor(Math.random() * left.length)];
      heard.current.push(pick);
      const r = await api.greeting(loc.state?.labSlug, pick, langRef.current, loc.state?.speaker);
      greeted.current = true;
      if (!r.text) return;
      setGreeting(r.text);
      if (readRef.current || !r.audio_b64) return;
      setState("speaking");
      await play({ b64: r.audio_b64 });
      setState("idle");
    } catch {
      greeted.current = true;
    }
  };

  // Typing is its own experience: no mic, no voice, his reply written on screen. Leaving it reopens
  // the flowing line rather than the old push-to-talk console.
  const setMode = (m) => {
    if (m === "text") return toText();
    setModeRaw("voice");
    setRead(localStorage.getItem("griot-read-mode") === "1");
    setConvo(true);
    openFlow();
  };

  const toText = () => {
    setNeedsTap(false);
    if (live.current) leaveFlow();
    setConvo(false);
    setRead(true);
    setModeRaw("text");
    setState("idle");
  };

  // Typing has no tools, so the plainest cases are caught here. Device-only, like the voice path.
  const catchName = (text) => {
    if (nameRef.current) return;
    const m = /(?:call me|my name is|i am|i'm|naitwa|jina langu ni)\s+([\p{Lu}][\p{L}'’-]{1,19})/u.exec(text || "");
    const name = m?.[1];
    if (!name || ["sorry", "not", "here", "fine", "good", "ok", "okay"].includes(name.toLowerCase())) return;
    nameRef.current = name;
    localStorage.setItem("griot-name", name);
  };

  const ask = async (text) => {
    if (!text || state !== "idle") return;
    setTurns((t) => [...t, { role: "user", text }]);
    catchName(text);
    await send(() => api.sendText(sessionId, text, !readRef.current, langRef.current, nameRef.current || undefined));
  };

  const onSend = () => {
    const text = draft.trim();
    if (!text) return;
    setDraft("");
    ask(text);
  };

  const toggleKeep = (id) =>
    setKept((k) => {
      const next = new Set(k);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  const end = () => {
    audio.current?.pause();
    live.current?.stop();
    live.current = null;
    stream.current?.getTracks().forEach((t) => t.stop());
    releaseMic();
    if (sessionId) api.endSession(sessionId).catch(() => {});
    // a sitting with something worth keeping earns a door, not a redirect
    if (shown.length) {
      setConvo(false);
      setState("idle");
      setClosing(true);
      return;
    }
    nav("/");
  };

  // In a flowing conversation the live line is re-pointed in place; nothing reconnects.
  const chooseLang = async (next) => {
    setLang(next);
    langRef.current = next;
    localStorage.setItem("griot-language", next);   // so the NEXT sitting opens in it too, welcome included
    greetings.current = [];
    if (live.current) await live.current.setLanguage(next);
    toast(next === "auto" ? "He'll follow the language you speak"
      : next === "en" ? "He'll stay in English" : "Atazungumza Kiswahili");
  };

  const cycleLang = () => {
    const next = LANGS[(LANGS.findIndex((l) => l.key === lang) + 1) % LANGS.length];
    setLang(next.key);
    toast(next.key === "auto" ? "Griot will follow the language you speak" : `Griot will speak ${next.label}`);
  };

  const props = {
    state,
    labName: loc.state?.labName || "British Council Kenya Lab",
    accent: loc.state?.accent || "#C87D55",
    traits: loc.state?.persona,
    showTranscript: read || !!loc.state?.showTranscript,
    sourcesAvailable: false,
    passages,
    togglePassages,
    read,
    toggleRead,
    stopSpeaking,
    openings,
    ask,
    mode, setMode, turns, draft, setDraft, onSend, onMic, end, cycleLang,
    langLabel: LANGS.find((l) => l.key === lang) || LANGS[0],
    convo, toggleConvo, cancelRecording,
    elapsed,
    setSource: setSheet,
    bottom,
    busy: state === "reflecting",
    aiConfigured: loc.state?.aiConfigured !== false,
    optIn: !!loc.state?.optIn,
  };

  return (
    <>
      <ConnectionBanner pending={!!pending} onRetry={() => pending?.()} accent={loc.state?.accent || "#C87D55"} />
      {closing ? (
        <Closing
          notes={shown}
          kept={kept}
          accent={loc.state?.accent || "#C87D55"}
          labName={props.labName}
          name={nameRef.current}
          onLeave={() => nav("/")}
        />
      ) : convo ? (
        <FlowView
          state={state}
          accent={loc.state?.accent || "#C87D55"}
          labName={props.labName}
          turns={turns}
          muted={muted}
          toggleMute={toggleMute}
          onInterrupt={() => {
            if (stateRef.current === "speaking") {            // it is talking: cut in
              if (live.current) return live.current.interrupt();
              return stopSpeaking();
            }
            live.current?.pullNote?.();                        // it is quiet: "show me that"
          }}
          leaveFlow={leaveFlow}
          end={end}
          showTranscript={props.showTranscript}
          showGreeting={!!loc.state?.showGreeting}
          greeting={greeting}
          notes={loc.state?.showNotes === false ? [] : notes}
          kept={kept}
          onKeep={toggleKeep}
          noteMotion={loc.state?.noteMotion !== false}
          volume={volume}
          onVolume={changeVolume}
          output={output}
          onOutput={changeOutput}
          ios={isIOS()}
          awayClosed={awayClosed}
          awayCopy={awayCopy}
          onResumeAway={resumeAway}
          allowText={loc.state?.allowText !== false}
          toText={toText}
          needsTap={needsTap}
          onWake={wakeAudio}
          lang={lang}
          onLang={chooseLang}
        />
      ) : isDesktop ? (
        <SessionDesktop {...props} />
      ) : (
        <SessionMobile {...props} />
      )}
      <SourceSheet source={sheet} onClose={() => setSheet(null)} />
    </>
  );
}
